import { useRouter } from "next/router";
import { useState } from "react";
import Icon from "~/assets/icons/Icon";
import Button from "~/components/Button";
import { Radio } from "~/components/Radio";
import { StoreCart as StoreCartType } from "~/shared-types";
import { prepareCheckout } from "~/strapi/api";
import { stringToCurrency } from "~/utils/stringConversion";
import { CartItem } from "../CartItem";
import { InputError } from "~/components/Backroom/InputError";
import Link from "next/link";
import { getRoute } from "~/utils/getRoute";
import { DeliverySection } from "./DeliverySection";
import { Provider } from "jotai";
import { useStoreCart } from "./useStoreCart";
import { AddCommentSection } from "./AddCommentSection";

const TOTAL_LIMIT = 999999.99;

export const StoreCart = ({ cart }: { cart: StoreCartType }) => {
  return (
    <Provider>
      <ProvidedStoreCart cart={cart} />
    </Provider>
  );
};

function ProvidedStoreCart(props: { cart: StoreCartType }) {
  const { cart } = props;
  const {
    currentDeliveryType,
    availableDeliveryTypes,
    hasFreeDelivery,
    setSavedValues,
    subTotal,
    total,
    deliveryPrice,
    canOnlyBePickedUp,
    receiverAddress,
    receiverName,
    orderComment,
    currentDeliveryLocation,
  } = useStoreCart(cart);

  const {
    items,
    store: {
      id: storeId,
      attributes: { deliveryType, name: storeName, freeDeliveryPurchasePrice },
    },
  } = cart;
  const router = useRouter();
  const [isWaitingForCheckout, setIsWaitingForCheckout] = useState(false);

  const onCheckout = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();

    /* istanbul ignore next */
    if (!currentDeliveryType) return;

    setIsWaitingForCheckout(true);

    const { checkoutUrl } = await prepareCheckout({
      cart,
      deliveryType: currentDeliveryType,
      deliveryPrice,
      receiverAddress: currentDeliveryLocation?.address ?? receiverAddress,
      receiverName,
      productCode: currentDeliveryLocation?.quote?.product_code,
      servicePointId: currentDeliveryLocation?.servicePointId,
      orderComment,
    });
    router.push(checkoutUrl);
  };

  // If the cart is empty, return null
  /* istanbul ignore next */
  if (!total) return null;

  const isTotalTooHigh = total > TOTAL_LIMIT;
  const hasMissingReceiver =
    currentDeliveryType !== "pickup" &&
    (!receiverName ||
      /* istanbul ignore next */
      !receiverAddress);
  const hasMissingShipmondoInput =
    currentDeliveryType === "shipmondo" &&
    /* istanbul ignore next */
    !currentDeliveryLocation?.quote?.price;

  return (
    <div>
      <h2 className="text-2xl mb-10">
        Din kurv hos{" "}
        <Link
          href={getRoute("/producenter/:slug", { slug: storeId })}
          className="text-primary"
        >
          {storeName}
        </Link>
      </h2>
      <div className="grid lg:grid-cols-2 gap-5 sm:gap-y-16 mb-40 lg:mb-28">
        <div>
          <ul className="lg:mr-20 xl:mr-16">
            {items.map((item) => (
              <CartItem key={item.product.id} item={item} />
            ))}
          </ul>
          <AddCommentSection />
        </div>

        <form className="bg-secondary-100 rounded-md -mx-5 md:-mx-20 lg:mx-0 px-5 md:px-20 lg:px-12 py-16 lg:py-20">
          <div className="flex flex-col gap-2 mb-20">
            {availableDeliveryTypes?.map((deliveryValues) => (
              <Radio
                key={`${deliveryValues.type}-${storeId}`}
                id={`${deliveryValues.type}-${storeId}`}
                groupName={`deliveryType-${storeId}`}
                checked={deliveryValues.type === currentDeliveryType}
                onChange={() =>
                  setSavedValues((prev) => ({
                    ...prev,
                    currentDeliveryType: deliveryValues.type,
                  }))
                }
              >
                {deliveryValues.label}
              </Radio>
            ))}
          </div>
          <DeliverySection storeDeliveryType={deliveryType} />
          <hr className="text-secondary-300 mb-20 mt-10 mx-2.5 xl:mx-0" />
          <div className="mb-6">
            <p className="flex justify-between">
              Subtotal
              <span>
                {stringToCurrency({
                  amount: subTotal,
                  prefix: "kr. ",
                })}
              </span>
            </p>
            {(currentDeliveryType === "shipping" ||
              currentDeliveryType === "freeShipping" ||
              currentDeliveryType === "shipmondo") &&
              deliveryType !== "none" &&
              (deliveryPrice || deliveryPrice === 0) && (
                <p className="flex justify-between">
                  Levering
                  <span>
                    {stringToCurrency({
                      amount: deliveryPrice,
                      prefix: "kr. ",
                    })}
                  </span>
                </p>
              )}
            <p className="flex justify-between mt-5">
              Pris i alt
              <span className="text-2xl">
                {stringToCurrency({
                  amount: total,
                  prefix: "kr. ",
                })}
              </span>
            </p>
          </div>
          <div className="flex flex-col justify-end items-center xs:items-end gap-2">
            <Button
              variant="primary"
              size="xl"
              onClick={onCheckout}
              disabled={
                isWaitingForCheckout ||
                isTotalTooHigh ||
                hasMissingReceiver ||
                hasMissingShipmondoInput
              }
            >
              {isWaitingForCheckout ? (
                <Icon name="spinner" className="animate-spin w-6 h-6" />
              ) : (
                "Gå til betaling"
              )}
            </Button>
            {total > TOTAL_LIMIT && (
              <InputError>
                Prisen må ikke overstige{" "}
                {stringToCurrency({ amount: TOTAL_LIMIT })}
              </InputError>
            )}
            {!canOnlyBePickedUp &&
            freeDeliveryPurchasePrice &&
            !hasFreeDelivery ? (
              <p className="font-medium text-secondary-600">
                Køb for{" "}
                {stringToCurrency({ amount: freeDeliveryPurchasePrice })} for at
                få gratis levering
              </p>
            ) : null}
          </div>
        </form>
      </div>
    </div>
  );
}
